/**
 * You Tab — Student workspace profile/settings view
 * Profile header, appearance, notifications, billing, achievements, sign out.
 * Extracted from StudentDashboardBeta (Phase 3.3f).
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import type { BffV2Response } from '../../types/bff-v2'

// ═══════════════════════════════════════════════════════════
// Types (local)
// ═══════════════════════════════════════════════════════════

type ThemeMode = 'light' | 'dark'

interface YouTabProps {
  data: BffV2Response
  theme: ThemeMode
  onThemeToggle: () => void
}

// ═══════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════

const YouTab: React.FC<YouTabProps> = ({ data, theme, onThemeToggle }) => {
  const { user, achievements } = data
  const { logout } = useAuth()
  const navigate = useNavigate()

  // Null-safe fallbacks
  const firstName = user?.first_name || 'Student'
  const avatarInitial = user?.avatar_initial || '?'
  const instrument = user?.instrument || ''
  const level = user?.level || ''

  const earnedCount = achievements.filter(a => a.earned).length

  return (
    <>
      {/* Profile Header */}
      <div className="flex items-center gap-[20px] p-[20px] bg-[var(--color-card)]
                      border border-[var(--color-border-light)] rounded-[var(--radius-r)]
                      shadow-[var(--shadow-sh0)] mb-[20px] flex-col sm:flex-row sm:items-center">
        <div className="w-[64px] h-[64px] rounded-full flex items-center justify-center text-[26px]
                        font-bold text-[var(--color-navy)] flex-shrink-0 border-[3px] border-white/40"
             style={{ background: 'linear-gradient(135deg, var(--color-cyan-light), var(--color-neon))' }}>
          {avatarInitial}
        </div>
        <div>
          <h2 className="text-[clamp(18px,2.5vw,23px)] font-bold text-[var(--color-text-primary)]">{firstName}</h2>
          <p className="text-[13px] text-[var(--color-text-tertiary)] mt-[2px]">
            {instrument} · {level}
          </p>
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[14px]">
        {/* Appearance */}
        <div className="bg-[var(--color-card)] border border-[var(--color-border-light)]
                        rounded-[var(--radius-r)] p-[16px] shadow-[var(--shadow-sh0)]">
          <h3 className="text-[13px] font-semibold text-[var(--color-text-secondary)]
                        uppercase tracking-[.4px] mb-[12px]">Appearance</h3>
          <div className="flex justify-between items-center py-[9px] border-b border-[var(--color-border-light)] last:border-b-0">
            <span className="text-[14px] font-medium text-[var(--color-text-primary)]">Theme</span>
            <div className="flex gap-[3px]">
              <button
                onClick={() => theme !== 'light' && onThemeToggle()}
                className={`px-[10px] py-[5px] border border-[var(--color-border-light)] rounded-[var(--radius-r-sm)]
                           text-[12px] font-medium transition-all duration-[.18s]
                           ${theme === 'light' ? 'bg-[var(--color-teal)] text-white border-[var(--color-teal)]' : 'text-[var(--color-text-tertiary)]'}`}>
                ☀️ Light
              </button>
              <button
                onClick={() => theme !== 'dark' && onThemeToggle()}
                className={`px-[10px] py-[5px] border border-[var(--color-border-light)] rounded-[var(--radius-r-sm)]
                           text-[12px] font-medium transition-all duration-[.18s]
                           ${theme === 'dark' ? 'bg-[var(--color-teal)] text-white border-[var(--color-teal)]' : 'text-[var(--color-text-tertiary)]'}`}>
                🌙 Dark
              </button>
            </div>
          </div>
          <div className="flex justify-between items-center py-[9px]">
            <span className="text-[14px] font-medium text-[var(--color-text-primary)]">Language</span>
            <span className="text-[13px] text-[var(--color-text-tertiary)]">🇺🇸 English ▾</span>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-[var(--color-card)] border border-[var(--color-border-light)]
                        rounded-[var(--radius-r)] p-[16px] shadow-[var(--shadow-sh0)]">
          <h3 className="text-[13px] font-semibold text-[var(--color-text-secondary)]
                        uppercase tracking-[.4px] mb-[12px]">Notifications</h3>
          {[
            { label: 'Email', on: true },
            { label: 'SMS', on: false },
            { label: 'Push', on: true },
          ].map((notif, i) => (
            <div key={i} className="flex justify-between items-center py-[9px] border-b border-[var(--color-border-light)] last:border-b-0">
              <span className="text-[14px] font-medium text-[var(--color-text-primary)]">{notif.label}</span>
              <div className={`w-[40px] h-[22px] rounded-[11px] relative cursor-pointer transition-all duration-[.2s] flex-shrink-0
                              ${notif.on ? 'bg-[var(--color-jade)]' : 'bg-[var(--color-g200)]'}`}>
                <div className={`absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,.08)]
                                transition-all duration-[.2s] ${notif.on ? 'left-[20px]' : 'left-[2px]'}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Billing */}
        <div className="bg-[var(--color-card)] border border-[var(--color-border-light)]
                        rounded-[var(--radius-r)] p-[16px] shadow-[var(--shadow-sh0)]">
          <h3 className="text-[13px] font-semibold text-[var(--color-text-secondary)]
                        uppercase tracking-[.4px] mb-[12px]">Billing</h3>
          {[
            { label: 'Plan', value: 'Symphony · $29/mo' },
            { label: 'Next billing', value: 'Jul 15, 2026' },
            { label: 'Payment', value: '•••• 4242' },
            { label: 'Status', value: 'Active', green: true },
          ].map((row, i) => (
            <div key={i} className="flex justify-between items-center py-[9px] border-b border-[var(--color-border-light)] last:border-b-0">
              <span className="text-[14px] font-medium text-[var(--color-text-primary)]">{row.label}</span>
              <span className={`text-[13px] ${row.green ? 'text-[var(--color-jade)] font-semibold' : 'text-[var(--color-text-tertiary)]'}`}>
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {/* Achievements */}
        <div className="bg-[var(--color-card)] border border-[var(--color-border-light)]
                        rounded-[var(--radius-r)] p-[16px] shadow-[var(--shadow-sh0)]">
          <h3 className="text-[13px] font-semibold text-[var(--color-text-secondary)]
                        uppercase tracking-[.4px] mb-[12px]">
            Achievements ({earnedCount}/{achievements.length})
          </h3>
          {achievements.length > 0 ? (
            <div className="grid grid-cols-2 gap-[5px] mt-[4px]">
              {achievements.map((a) => (
                <div key={a.id}
                     className={`flex flex-col items-center gap-[2px] p-[7px] bg-[var(--color-card2)]
                                rounded-[12px] text-[18px]
                                ${a.earned ? 'border border-[rgba(0,167,142,.2)]' : 'opacity-30 grayscale'}`}>
                  {a.icon}
                  <span className="text-[10px] font-semibold text-[var(--color-text-tertiary)]">{a.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-[20px]">
              <div className="text-[32px] mb-[8px]">🏆</div>
              <div className="text-[12px] text-[var(--color-text-tertiary)]">
                Practice regularly to earn achievements
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sign Out */}
      <div className="mt-[20px] text-center p-[20px]">
        <button
          onClick={() => logout()}
          className="px-[28px] py-[10px] bg-[var(--color-rose-accent)] text-white text-[14px] font-semibold
                     rounded-[var(--radius-r-sm)] hover:opacity-90 transition-all duration-[.18s]">
          Sign Out
        </button>
      </div>
    </>
  )
}

export default YouTab
export type { YouTabProps }
